import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useConfirmPartsConsumption } from '@/hooks/useInventory';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';

interface WorkOrderPartRow {
  /** A `work_order_parts.id` — parts-status `consumableParts[].workOrderPartId` (#3270). */
  id: string;
  partName: string;
  partSku: string;
  quantity: number;
}

interface PartsConsumptionDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderId: string;
  parts: WorkOrderPartRow[];
}

interface PartDecision {
  action: 'consumed' | 'leftover';
  quantityConsumed: number;
}

/** confirm-parts-consumption refusal reasons (#3270) → inventory namespace keys. */
const REFUSAL_MESSAGE_KEYS: Record<string, string> = {
  work_order_part_not_found: 'transitWarehouse.confirmConsumption.partsChanged',
  work_order_not_found: 'transitWarehouse.confirmConsumption.partsChanged',
  already_confirmed: 'transitWarehouse.confirmConsumption.alreadyConfirmed',
  quantity_exceeds_assigned: 'transitWarehouse.confirmConsumption.quantityExceeds',
  insufficient_stock: 'transitWarehouse.confirmConsumption.insufficientStock',
  quantity_required: 'transitWarehouse.confirmConsumption.quantityRequired',
  no_stock_source: 'transitWarehouse.confirmConsumption.noStockSource',
  ambiguous_stock_source: 'transitWarehouse.confirmConsumption.ambiguousStockSource',
  stock_source_not_transit: 'transitWarehouse.confirmConsumption.stockSourceNotTransit',
};

function refusalMessageKey(error: unknown): string {
  const reason =
    error instanceof ApiError && error.data && typeof error.data === 'object' && 'reason' in error.data
      ? (error.data as { reason?: unknown }).reason
      : undefined;
  return (typeof reason === 'string' ? REFUSAL_MESSAGE_KEYS[reason] : undefined)
    ?? 'transitWarehouse.confirmConsumption.failed';
}

export function PartsConsumptionDialog({ open, onClose, workOrderId, parts }: PartsConsumptionDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const confirmConsumption = useConfirmPartsConsumption();

  const [decisions, setDecisions] = useState<Record<string, PartDecision>>(() => {
    const initial: Record<string, PartDecision> = {};
    for (const part of parts) {
      initial[part.id] = { action: 'consumed', quantityConsumed: part.quantity };
    }
    return initial;
  });

  const handleActionChange = useCallback((partId: string, action: 'consumed' | 'leftover') => {
    setDecisions((prev) => ({
      ...prev,
      [partId]: {
        ...prev[partId],
        action,
        quantityConsumed: action === 'consumed' ? (parts.find(p => p.id === partId)?.quantity ?? 0) : 0,
      },
    }));
  }, [parts]);

  const handleQuantityChange = useCallback((partId: string, qty: number) => {
    setDecisions((prev) => {
      const existing = prev[partId] ?? { action: 'consumed' as const, quantityConsumed: 0 };
      return { ...prev, [partId]: { ...existing, quantityConsumed: qty } };
    });
  }, []);

  const handleConfirm = () => {
    // Build the payload from the rows on screen, not from the decisions map:
    // the map is seeded once, so it can hold ids no longer offered (#3270).
    const partsPayload = parts.map((part) => {
      const decision = decisions[part.id] ?? { action: 'consumed' as const, quantityConsumed: part.quantity };
      const quantityConsumed = decision.action === 'consumed' ? Math.min(decision.quantityConsumed, part.quantity) : 0;
      // Zero usage is a leftover: the server refuses a zero "consumed" catalog part.
      const action: PartDecision['action'] = quantityConsumed > 0 ? 'consumed' : 'leftover';
      return { workOrderPartId: part.id, quantityConsumed, action };
    });

    confirmConsumption.mutate(
      { workOrderId, parts: partsPayload },
      {
        onSuccess: () => {
          notify.success(t('transitWarehouse.confirmConsumption.success'));
          onClose();
        },
        onError: (error: unknown) => {
          notify.error(tc('error'), t(refusalMessageKey(error)));
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('transitWarehouse.confirmConsumption.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('transitWarehouse.confirmConsumption.description')}
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('partNumber')}</TableCell>
                <TableCell align="right">{t('quantity')}</TableCell>
                <TableCell>{t('stockStatus.label')}</TableCell>
                <TableCell align="right">{t('transitWarehouse.confirmConsumption.quantityUsed')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parts.map((part) => {
                const decision = decisions[part.id];
                return (
                  <TableRow key={part.id}>
                    <TableCell>{part.partName}</TableCell>
                    <TableCell>{part.partSku || '—'}</TableCell>
                    <TableCell align="right">{part.quantity}</TableCell>
                    <TableCell>
                      <ToggleButtonGroup
                        value={decision?.action ?? 'consumed'}
                        exclusive
                        onChange={(_, v) => { if (v) handleActionChange(part.id, v); }}
                        size="small"
                      >
                        <ToggleButton value="consumed">{t('transitWarehouse.confirmConsumption.consumed')}</ToggleButton>
                        <ToggleButton value="leftover">{t('transitWarehouse.confirmConsumption.leftover')}</ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell align="right">
                      {decision?.action === 'consumed' && (
                        <TextField
                          type="number"
                          size="small"
                          value={decision.quantityConsumed}
                          onChange={(e) => handleQuantityChange(part.id, Math.min(part.quantity, Math.max(0, parseInt(e.target.value) || 0)))}
                          inputProps={{ min: 0, max: part.quantity, style: { width: 60, textAlign: 'right' } }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmConsumption.isPending}
          startIcon={confirmConsumption.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('transitWarehouse.confirmConsumption.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
